import {isString} from 'lodash';

import {ActionWithPayload} from '../../helpers/reduxHelpers';
import {ThunkAction} from '../appThunk';
import {AccountDetailsPanelAction} from './accounts/accountDetailsPanelActions';
import {CreateAccountFormAction} from './accounts/createAccountForm/createAccountFormActions';
import {AnalyticsPanelAction} from './analytics/analyticsPanelActions';
import {AppPanelAction} from './app/appPanelActions';
import {CalendarsPanelAction} from './calendars/calendarsPanelActions';
import {CellsPanelAction} from './cells/cellsPanelActions';
import {ComposerPanelAction} from './composer/composerPanelActions';
import {ContactCardAction} from './contacts/contactCard/contactCardPanelActions';
import {ContactDetailsPanelAction} from './contacts/contactDetailsPanelActions';
import {ContactsManagerPanelAction} from './contacts/contactsManager/contactsManagerPanelActions';
import {CrmPreviewAction} from './contacts/contactsManager/crmPreview/crmPreviewActions';
import {CreateContactFormAction} from './contacts/createContactForm/createContactFormActions';
import {EmlPanelAction} from './eml/emlPanelActions';
import {InboxesPanelAction} from './inboxes/inboxesPanelActions';
import {LegacyPanelAction} from './legacy/legacyPanelActions';
import {PanelState} from './panelsState';
import {NewPanelId} from './panelStateBase';
import {SettingsPanelAction} from './settings/settingsPanelActions';
import {ViewerPanelAction} from './viewer/viewerPanelActions';

/*
 * Type enum.
 */

export enum PanelActionsEnum {
  SET_PANEL = '@@panels/SET_PANEL',
  REMOVE_PANEL = '@@panels/REMOVE_PANEL',
}

/*
 * Types.
 */

export interface SetPanelAction extends ActionWithPayload<PanelState> {
  type: PanelActionsEnum.SET_PANEL;
}

export interface RemovePanelAction extends ActionWithPayload<NewPanelId> {
  type: PanelActionsEnum.REMOVE_PANEL;
}

/*
 * Action templates.
 */

export interface PanelActionPayload<TId extends NewPanelId> {
  panelId: TId;
}

/*
 * Type guard.
 */

export function isPanelAction(action: any): action is ActionWithPayload<PanelActionPayload<NewPanelId>> {
  return Boolean(action.payload && isString(action.payload.panelId));
}

/*
 * Action creators.
 */

export function setPanelAction(state: PanelState): SetPanelAction {
  return {
    type: PanelActionsEnum.SET_PANEL,
    payload: state,
  };
}

export function removePanelAction(id: NewPanelId): ThunkAction {
  return (dispatch, getState) => {
    // Check if a panel with the provided ID exists.
    const {panels} = getState();
    if (!panels[id]) {
      return null;
    }

    // If so, queue an action to remove it.
    const action: RemovePanelAction = {
      type: PanelActionsEnum.REMOVE_PANEL,
      payload: id,
    };
    return dispatch(action);
  };
}

/*
 * Aggregate type.
 */

export type PanelsAction =
  | SetPanelAction
  | RemovePanelAction
  | AppPanelAction
  | InboxesPanelAction
  | CellsPanelAction
  | ViewerPanelAction
  | ComposerPanelAction
  | CalendarsPanelAction
  | AnalyticsPanelAction
  | EmlPanelAction
  | ContactDetailsPanelAction
  | ContactsManagerPanelAction
  | SettingsPanelAction
  | LegacyPanelAction
  | AccountDetailsPanelAction
  | CreateContactFormAction
  | CreateAccountFormAction
  | ContactCardAction
  | CrmPreviewAction;
